/**
 * DryRunModal — per-rule metadata + projection viewer.
 *
 * Conceptual model:
 *   A dry-run is the projection of every iteration the rule will produce
 *   over its lifespan — periods × materialised geographies. One row per
 *   period × geography, each row carries: scheduled date, geography,
 *   assignee role, source obligation reference.
 *
 * Layout:
 *   1. Rule metadata — one sub-card per persistence row from the editor
 *      (Identity, Source obligation, Authority/Roles, Trigger, Scope,
 *      Applicability, Date exceptions, Parameters). Each sub-card lists
 *      EVERY field that section persists, with `—` for nulls and a
 *      "saved / partial / not configured" chip so the operator can
 *      pinpoint what came back from the DB.
 *   2. Iterations table — flat period × geography cross-product. Empty
 *      → concrete blocker list, not a generic banner.
 *
 * Read-only. No engine call; no register writes.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { RefreshCw, ChevronDown } from 'lucide-react';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import Modal from '../common/Modal';
import Button from '../common/Button';
import extractApiError from '../../utils/apiError';
import {
  CadenceRule,
  ScopeConfig,
  ScopeLocation,
  TriggerConfig,
  ApplicabilityConfig,
  DateExceptionConfig,
  RuleParametersConfig,
  DryRunRow,
  CadenceRole,
  getRule,
  getRuleScope,
  getRuleTrigger,
  getRuleApplicability,
  getRuleDateExceptions,
  getRuleParameters,
  getRuleDryRun,
  getRoles,
} from '../../services/cadenceService';

interface Props {
  rule: CadenceRule;
  onClose: () => void;
}

// ============================================================
// Display helpers
// ============================================================

const DASH = '—';
const show = (v: any) => (v === null || v === undefined || v === '' ? DASH : String(v));

// LOCAL calendar date as YYYY-MM-DD (never toISOString — that is UTC and
// yields yesterday before 05:30 IST). Used for the future-start comparison,
// where rule.start_date is a date-only 'YYYY-MM-DD' string.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtBusinessDays = (days?: number[] | null) => {
  if (!days || days.length === 0) return DASH;
  const names = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return days.map(d => names[d] || `?${d}`).join(', ');
};

// (Window-classification helpers removed — the backend now walks the rule's
// lifespan directly, so the frontend no longer needs to filter or annotate
// in-window vs out-of-window rows.)

// Persistence status for each sub-card.
type SaveStatus = 'saved' | 'partial' | 'unsaved';
const StatusChip: React.FC<{ status: SaveStatus; note?: string }> = ({ status, note }) => {
  const label =
    status === 'saved' ? 'saved' :
    status === 'partial' ? 'partial' : 'not configured';
  const variant =
    status === 'saved' ? 'success' :
    status === 'partial' ? 'warning' : 'neutral';
  return <span className={`status-badge sm ${variant}`} title={note || ''}>{label}</span>;
};

// Trigger health classification — used both for the chip and for the
// metadata-row "warn" highlighting.
const triggerStatus = (t: TriggerConfig | null): SaveStatus => {
  if (!t) return 'unsaved';
  if (t.mode === 'calendar') return t.frequency ? 'saved' : 'partial';
  if (t.mode === 'event') {
    if (!t.event_id || !t.variant) return 'partial';
    if (t.variant === 'after_event' && (t.offset_value == null || !t.offset_unit || !t.anchor_from)) return 'partial';
    return 'saved';
  }
  return 'unsaved';
};

// ============================================================
// Metadata-row component
// ============================================================

const MetadataRow: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: 'warn' | 'muted';
}> = ({ label, value, tone }) => (
  <div className={`dry-meta-row ${tone || ''}`}>
    <div className="dry-meta-label">{label}</div>
    <div className="dry-meta-value">
      {value === undefined || value === null || value === '' || value === DASH
        ? <span className="dry-meta-muted">{DASH}</span>
        : value}
    </div>
  </div>
);

// Reusable sub-card wrapper. Collapsible — defaults to closed so the
// stats + iterations table sit above the fold; users expand only what
// they want to inspect.
const SubCard: React.FC<{
  number: number;
  title: string;
  status: SaveStatus;
  note?: string;
  defaultOpen?: boolean;
  grid?: boolean;          // false = body is not a key/value grid (e.g. a table)
  children: React.ReactNode;
}> = ({ number, title, status, note, defaultOpen = false, grid = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`collapse-section collapse-section--l1 ${open ? 'cs-open' : 'cs-closed'}`}>
      <div
        className="collapse-section__header"
        onClick={() => setOpen(o => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        aria-expanded={open}
      >
        <span className="collapse-section__toggle"><ChevronDown size={14} /></span>
        <div className="collapse-section__title-block">
          <span className="collapse-section__code">{number}.</span>
          <span className="collapse-section__name">{title}</span>
        </div>
        <div className="collapse-section__meta">
          <StatusChip status={status} note={note} />
        </div>
      </div>
      {open && (
        <div className="collapse-section__body">
          {grid ? <div className="dry-meta-grid">{children}</div> : children}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Main
// ============================================================

const DryRunModal: React.FC<Props> = ({ rule: initialRule, onClose }) => {
  const { formatDate, formatDateTime } = useEntitySettings();
  // Entity-formatted display helpers — keep the muted em-dash for empties.
  const fmtDate = (s?: string | null) => (s ? formatDate(s) : DASH);
  const fmtDT = (s?: string | null) => (s ? formatDateTime(s) : DASH);
  const [rule, setRuleData] = useState<CadenceRule>(initialRule);
  const [scope, setScope] = useState<ScopeConfig | null>(null);
  const [trigger, setTrigger] = useState<TriggerConfig | null>(null);
  const [applicability, setApplicability] = useState<ApplicabilityConfig | null>(null);
  const [dateExceptions, setDateExceptions] = useState<DateExceptionConfig | null>(null);
  const [parameters, setParameters] = useState<RuleParametersConfig | null>(null);
  const [periods, setPeriods] = useState<DryRunRow[]>([]);
  const [roles, setRoles] = useState<CadenceRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Track whether each per-rule fetch returned a real row (not a thrown 404
  // we caught). The endpoints return `data: null` when no row exists, so we
  // can't always tell from the result alone — we set the explicit flag in
  // the try/catch chain.
  const [dxLoaded, setDxLoaded] = useState(false);
  const [paramsLoaded, setParamsLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setDxLoaded(false);
    setParamsLoaded(false);
    try {
      const [r, sc, tr, ap, dx, pa, pr, rl] = await Promise.all([
        getRule(initialRule.id).catch(() => initialRule),
        getRuleScope(initialRule.id).catch(() => null),
        getRuleTrigger(initialRule.id).catch(() => null),
        getRuleApplicability(initialRule.id).catch(() => null),
        getRuleDateExceptions(initialRule.id)
          .then(v => { setDxLoaded(true); return v; })
          .catch(() => null),
        getRuleParameters(initialRule.id)
          .then(v => { setParamsLoaded(true); return v; })
          .catch(() => null),
        // No `months` cap — backend walks the rule's lifespan (start_date →
        // end_date, with a 5-year safety cap for open-ended rules).
        getRuleDryRun(initialRule.id).then(res => res.rows || []).catch(() => []),
        getRoles().catch(() => []),
      ]);
      setRuleData(r);
      setScope(sc);
      setTrigger(tr);
      setApplicability(ap);
      setDateExceptions(dx);
      setParameters(pa);
      setPeriods(pr);
      setRoles(rl);
    } catch (e: any) {
      setError(extractApiError(e, 'Failed to load rule data.'));
    } finally {
      setLoading(false);
    }
  }, [initialRule]);

  useEffect(() => { load(); }, [load]);

  // ---------- derived: iterations ----------

  const activeLocations: ScopeLocation[] = useMemo(
    () => (scope?.locations || []).filter(l => l.status === 'active'),
    [scope],
  );
  const missingLocations: ScopeLocation[] = useMemo(
    () => (scope?.locations || []).filter(l => l.status === 'missing'),
    [scope],
  );

  // ---------- derived: symbolic event-mode due-date expression ----------

  // For event-mode after_event: `x + 14 days` / `x - 7 days`.
  // For event-mode watch_not_happened: deterministic deadline computed
  // per period (period_start + deadline_offset_days) — handled per-row.
  const symbolicDueExpression = useMemo(() => {
    if (!trigger || trigger.mode !== 'event') return null;
    if (trigger.variant === 'after_event') {
      if (trigger.offset_value == null || !trigger.offset_unit) return 'x';
      const sign = trigger.offset_value < 0 ? '−' : '+';
      const mag = Math.abs(trigger.offset_value);
      return `x ${sign} ${mag} ${trigger.offset_unit}`;
    }
    return null; // watch_not_happened renders a per-row computed date
  }, [trigger]);

  // ---------- derived: iterations (period × geography cross-product) ----------

  type Iteration = {
    idx: number;
    period_token: string;
    period_start?: string;
    period_end?: string;
    original_date?: string | null;
    final_date?: string | null;
    shifted: boolean;
    shift_reason?: string | null;
    is_event: boolean;
    location_id: number | null;
    location_name: string;
    location_type: string | null;
  };
  const iterations: Iteration[] = useMemo(() => {
    if (periods.length === 0 || activeLocations.length === 0) return [];
    const out: Iteration[] = [];
    let i = 1;
    for (const p of periods) {
      for (const l of activeLocations) {
        out.push({
          idx: i++,
          period_token: p.period_token,
          period_start: p.period_start,
          period_end: p.period_end,
          original_date: p.original_date,
          final_date: p.final_date,
          shifted: p.shifted,
          shift_reason: p.reason,
          is_event: p.mode === 'event',
          location_id: l.location_id,
          location_name: l.location_name || `loc #${l.location_id}`,
          location_type: l.location_type || null,
        });
      }
    }
    return out;
  }, [periods, activeLocations]);

  // Truncation: render first 100 + last 100 only. The verification value
  // sits at the edges of the lifespan walk; the middle is repetitive.
  const TRUNC_HEAD = 100;
  const TRUNC_TAIL = 100;
  const truncated = iterations.length > (TRUNC_HEAD + TRUNC_TAIL);
  const headRows = truncated ? iterations.slice(0, TRUNC_HEAD) : iterations;
  const tailRows = truncated ? iterations.slice(iterations.length - TRUNC_TAIL) : [];
  const omittedCount = truncated ? iterations.length - TRUNC_HEAD - TRUNC_TAIL : 0;

  // Applicability cell — same for every row of the same rule.
  const applicabilityCell = useMemo(() => {
    const mode = applicability?.mode || rule.applicability_mode || 'unconditional';
    if (mode === 'unconditional') return <span className="dry-meta-muted">unconditional</span>;
    if (mode === 'threshold') {
      const name = applicability?.threshold?.name || (rule.applicability_threshold_id ? `#${rule.applicability_threshold_id}` : '?');
      return (
        <span className="dry-pill dry-cond-pill" title={`conditional · threshold: ${name}`}>
          ⓘ conditional · threshold: {name}
        </span>
      );
    }
    if (mode === 'questionnaire') {
      const name = applicability?.questionnaire?.name || (rule.applicability_questionnaire_id ? `#${rule.applicability_questionnaire_id}` : '?');
      const n = applicability?.conditions?.length ?? 0;
      return (
        <span className="dry-pill dry-cond-pill" title={`conditional · questionnaire: ${name} · ${n} condition(s)`}>
          ⓘ conditional · questionnaire: {name}{n ? ` · ${n} cond` : ''}
        </span>
      );
    }
    return <span className="dry-meta-muted">{mode}</span>;
  }, [applicability, rule]);

  // Render the Due-date cell for one iteration, with the right semantics
  // for calendar / event-after / event-watch_not_happened.
  const renderDueCell = useCallback((it: Iteration): React.ReactNode => {
    // Calendar mode — concrete original/final, with shift sub-line.
    if (!it.is_event) {
      return (
        <>
          <strong>{formatDate(it.final_date)}</strong>
          {it.shifted && (
            <div className="dry-shifted-sub">
              shifted from {formatDate(it.original_date)}{it.shift_reason ? ` · ${it.shift_reason}` : ''}
            </div>
          )}
        </>
      );
    }
    // Event watch_not_happened — deadline = period_start + deadline_offset (offset variant)
    // or trigger.deadline_date (absolute).
    if (trigger?.variant === 'watch_not_happened') {
      let deadline: string | null = null;
      if (trigger.deadline_kind === 'absolute' && trigger.deadline_date) {
        deadline = trigger.deadline_date;
      } else if (trigger.deadline_kind === 'offset' && trigger.deadline_offset_days != null && it.period_start) {
        const ref = trigger.deadline_reference === 'period_end' && it.period_end
          ? it.period_end : it.period_start;
        const d = new Date(ref);
        d.setUTCDate(d.getUTCDate() + (trigger.deadline_offset_days || 0));
        deadline = d.toISOString().slice(0, 10);
      }
      return (
        <>
          <strong>{deadline ? formatDate(deadline) : '—'}</strong>
          <div className="dry-shifted-sub">fires only if anchor has not occurred by this date</div>
        </>
      );
    }
    // Event after_event — symbolic `x ± N <unit>`.
    return (
      <>
        <strong className="dry-symbolic">{symbolicDueExpression || 'x'}</strong>
        <div className="dry-shifted-sub">x = anchor event date</div>
      </>
    );
  }, [trigger, symbolicDueExpression, formatDate]);

  const blockers: string[] = useMemo(() => {
    const list: string[] = [];
    if (!trigger) {
      list.push('Trigger is not configured. Open the rule editor → Trigger section, pick Calendar or Event mode, save.');
    } else if (trigger.mode === 'calendar' && !trigger.frequency) {
      list.push('Trigger is Calendar mode but no frequency is set. Pick daily / weekly / monthly / quarterly / annual / … and save.');
    } else if (trigger.mode === 'event' && !trigger.variant) {
      list.push('Trigger is Event mode but variant is not set. Pick after_event or watch_not_happened and save.');
    }
    if (!scope || activeLocations.length === 0) {
      list.push('No active geographies in the materialised scope list. Configure scope in the rule editor and save so the engine has at least one location to fire on.');
    }
    if (rule.start_date && rule.end_date && rule.start_date > rule.end_date) {
      list.push(`Date window is invalid: start_date (${rule.start_date}) is after end_date (${rule.end_date}).`);
    }
    if (trigger?.mode === 'calendar' && trigger.frequency && periods.length === 0 && (scope?.locations?.length ?? 0) > 0) {
      list.push(`The trigger produced no periods inside the rule's lifespan [${rule.start_date || 'open'} … ${rule.end_date || 'open'}]. Check the frequency / specifier and the date window.`);
    }
    return list;
  }, [trigger, scope, activeLocations, rule.start_date, rule.end_date, periods]);

  // ---------- role name resolution ----------

  const roleName = useCallback((id?: number, fallback?: string) => {
    if (fallback) return fallback;
    if (id == null) return '';
    const r = roles.find(x => x.id === id);
    return r ? r.name : '';
  }, [roles]);

  const accountableRoleName = useMemo(
    () => roleName(rule.accountable_role_id, rule.accountable_role_name),
    [rule, roleName],
  );
  const fallbackRoleName = useMemo(
    () => roleName(rule.fallback_role_id, rule.fallback_role_name),
    [rule, roleName],
  );

  // ---------- save-status per sub-card ----------

  const trigStat = triggerStatus(trigger);
  const scopeStat: SaveStatus = !scope
    ? 'unsaved'
    : activeLocations.length === 0
      ? 'partial'
      : 'saved';
  const applStat: SaveStatus = !applicability ? 'unsaved' : 'saved';
  const dxStat: SaveStatus = dxLoaded
    ? (dateExceptions ? 'saved' : 'partial')
    : (dateExceptions ? 'saved' : 'unsaved');
  const paramsStat: SaveStatus = parameters
    ? (parameters.mode === 'modify' ? 'saved' : 'saved')
    : (paramsLoaded ? 'partial' : 'unsaved');

  // Date window in days (purely informational)
  const windowDays = useMemo(() => {
    if (!rule.start_date || !rule.end_date) return null;
    const a = new Date(rule.start_date).getTime();
    const b = new Date(rule.end_date).getTime();
    return Math.round((b - a) / (24 * 3600 * 1000)) + 1;
  }, [rule.start_date, rule.end_date]);

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <Modal.Header>
          Dry-run · Rule #{rule.id} · projection over the rule's lifespan - 
        {rule.obligation_name || `Rule #${rule.id}`}
      </Modal.Header>
      <Modal.Body>
        {loading && (
            <div className="dry-loading">
              <div className="dry-spinner" />
              <div>Loading rule + scope + trigger + applicability + date-exceptions + parameters + periods…</div>
            </div>
          )}

          {!loading && error && <div className="alert-error">{error}</div>}

          {!loading && !error && (
            <>
              {/* ====================================================
                  PRIMARY — Projected iterations
                  Sits at the top so the operator sees the result first.
                  Stats tiles + truncated table.
                  ==================================================== */}
              <section className="dry-iter-card">
                <h4 className="dry-section-title">
                  Projected iterations
                  <span className="dry-readonly-tag">read-only · lifespan walk</span>
                </h4>

                {/* Stat tiles — these are the numbers the operator confirms */}
                <div className="dry-stats" style={{ marginTop: '.5rem' }}>
                  <div className="dry-stat">
                    <div className="num">{periods.length}</div>
                    <div className="label">Due dates (periods)</div>
                  </div>
                  <div className="dry-stat">
                    <div className="num">{activeLocations.length}</div>
                    <div className="label">Active geographies</div>
                  </div>
                  <div className="dry-stat blue">
                    <div className="num">{iterations.length}</div>
                    <div className="label">Total iterations</div>
                  </div>
                  <div className="dry-stat">
                    <div className="num" style={{ fontSize: '1rem' }}>
                      {trigger?.mode === 'calendar'
                        ? (trigger?.frequency || '—')
                        : (trigger?.mode === 'event' ? `event · ${trigger.variant || '?'}` : '—')}
                    </div>
                    <div className="label">Trigger</div>
                  </div>
                  <div className="dry-stat">
                    <div className="num" style={{ fontSize: '1rem' }}>
                      {rule.start_date ? formatDate(rule.start_date) : '—'} → {rule.end_date ? formatDate(rule.end_date) : `+5y (cap)`}
                    </div>
                    <div className="label">Lifespan</div>
                  </div>
                </div>

                {iterations.length > 0 ? (
                  <>
                    {truncated && (
                      <div className="dry-trunc-note">
                        Showing first {TRUNC_HEAD} + last {TRUNC_TAIL} of {iterations.length} iterations
                        ({omittedCount} omitted in the middle for legibility).
                      </div>
                    )}
                    <div className="dry-iter-wrap">
                      <table className="dry-iter-table">
                        <thead>
                          <tr>
                            <th className="table-list-th table-list-cell--sno">S.No</th>
                            <th>Period</th>
                            <th>Due date</th>
                            <th>Applicability</th>
                            <th>Geography</th>
                            <th>Assignee authority</th>
                            <th>Source / template</th>
                          </tr>
                        </thead>
                        <tbody>
                          {headRows.map(it => (
                            <tr key={`h-${it.idx}-${it.location_id ?? 'none'}`}>
                              <td>{it.idx}</td>
                              <td><code>{it.period_token}</code></td>
                              <td>{renderDueCell(it)}</td>
                              <td>{applicabilityCell}</td>
                              <td>
                                {it.location_name}
                                {it.location_type && (<div className="dry-meta-sub">{it.location_type}</div>)}
                              </td>
                              <td>
                                {accountableRoleName || <span className="dry-meta-muted">unassigned</span>}
                                {fallbackRoleName && (<div className="dry-meta-sub">fallback: {fallbackRoleName}</div>)}
                              </td>
                              <td>
                                <div>{rule.obligation_name || DASH}</div>
                                {rule.template_reference && (
                                  <code className="dry-meta-sub">{rule.template_reference}</code>
                                )}
                              </td>
                            </tr>
                          ))}
                          {truncated && (
                            <tr className="dry-trunc-sep">
                              <td colSpan={7}>
                                ⋯ {omittedCount} iteration{omittedCount === 1 ? '' : 's'} omitted ⋯
                              </td>
                            </tr>
                          )}
                          {tailRows.map(it => (
                            <tr key={`t-${it.idx}-${it.location_id ?? 'none'}`}>
                              <td>{it.idx}</td>
                              <td><code>{it.period_token}</code></td>
                              <td>{renderDueCell(it)}</td>
                              <td>{applicabilityCell}</td>
                              <td>
                                {it.location_name}
                                {it.location_type && (<div className="dry-meta-sub">{it.location_type}</div>)}
                              </td>
                              <td>
                                {accountableRoleName || <span className="dry-meta-muted">unassigned</span>}
                                {fallbackRoleName && (<div className="dry-meta-sub">fallback: {fallbackRoleName}</div>)}
                              </td>
                              <td>
                                <div>{rule.obligation_name || DASH}</div>
                                {rule.template_reference && (
                                  <code className="dry-meta-sub">{rule.template_reference}</code>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="dry-blocker-block">
                    <p className="dry-blocker-lead">No iterations can be projected for this rule yet. Reasons:</p>
                    {blockers.length > 0 ? (
                      <ol className="dry-blocker-list">
                        {blockers.map((b, i) => <li key={i}>{b}</li>)}
                      </ol>
                    ) : (
                      <p className="dry-blocker-lead" style={{ color: '#64748b' }}>
                        Configuration looks complete but the projection endpoint returned no periods.
                        Verify the Trigger section settings.
                      </p>
                    )}
                  </div>
                )}
              </section>

              {/* ====================================================
                  SECONDARY — Rule metadata (diagnostics)
                  Numbered + collapsed by default. The operator opens
                  only what they want to inspect.
                  ==================================================== */}
              <h4 className="dry-section-title" style={{ marginTop: '1.25rem' }}>
                Rule metadata
                <span className="dry-readonly-tag">
                  9 sections · all collapsed · click any header to inspect persisted fields
                </span>
              </h4>

              {/* 1. Identity */}
              <SubCard number={1} title="Identity (SCH_cadence_rules)" status="saved">
                <MetadataRow label="Rule ID" value={`#${rule.id}`} />
                <MetadataRow label="Entity ID" value={`#${rule.entity_id}`} />
                <MetadataRow label="Status" value={<span className={`dry-pill s-${rule.rule_status.toLowerCase().replace(/[^a-z]/g, '')}`}>{rule.rule_status}</span>} />
                <MetadataRow label="is_active" value={String(rule.is_active)} />
                <MetadataRow label="Category" value={`${rule.category_name || ''} (#${rule.category_id})`} />
                <MetadataRow label="Start date" value={fmtDate(rule.start_date)} tone={rule.start_date && rule.start_date > todayISO() ? 'warn' : undefined} />
                <MetadataRow label="End date" value={fmtDate(rule.end_date)} />
                <MetadataRow label="Window length" value={windowDays != null ? `${windowDays} day${windowDays === 1 ? '' : 's'}` : DASH} />
                <MetadataRow label="Created on" value={fmtDT(rule.created_on)} tone="muted" />
                <MetadataRow label="Modified on" value={fmtDT(rule.modified_on)} tone="muted" />
              </SubCard>

              {/* 2. Source obligation */}
              <SubCard number={2} title="Source obligation (mirror)" status="saved">
                <MetadataRow label="Source module" value={rule.source_module} />
                <MetadataRow label="Source type" value={rule.source_type} />
                <MetadataRow label="Source obligation ID" value={rule.source_obligation_id ? `#${rule.source_obligation_id}` : ''} />
                <MetadataRow label="Obligation name" value={rule.obligation_name} />
                <MetadataRow label="Obligation description" value={rule.obligation_description} />
                <MetadataRow label="Template reference" value={rule.template_reference ? <code>{rule.template_reference}</code> : ''} />
                <MetadataRow label="Regulatory source" value={rule.regulatory_source} />
                <MetadataRow label="Reference URL" value={rule.reference_url ? <a href={rule.reference_url} target="_blank" rel="noreferrer">{rule.reference_url}</a> : ''} />
                <MetadataRow label="Policy ref" value={rule.policy_ref} />
              </SubCard>

              {/* 3. Authorities (accountable / fallback) — sourced from ORG_authorities */}
              <SubCard
                number={3}
                title="Authorities (ORG_authorities · accountable & fallback)"
                status={rule.accountable_role_id ? 'saved' : 'partial'}
                note={rule.accountable_role_id ? '' : 'Accountable authority is required for activation'}
              >
                <MetadataRow
                  label="Accountable authority"
                  value={rule.accountable_role_id ? `${accountableRoleName || '?'} (#${rule.accountable_role_id})` : ''}
                  tone={!rule.accountable_role_id ? 'warn' : undefined}
                />
                <MetadataRow label="Fallback authority" value={rule.fallback_role_id ? `${fallbackRoleName || '?'} (#${rule.fallback_role_id})` : ''} />
                <MetadataRow label="Fulfilment handler" value={rule.fulfillment_handler} />
              </SubCard>

              {/* 4. Due-date logic — Trigger */}
              <SubCard
                number={4}
                title={`Due-date logic — Trigger (SCH_rule_triggers) ${
                  trigger ? '· ' + (trigger.mode === 'calendar' ? 'Calendar-driven' : 'Event-driven') : ''
                }`}
                status={trigStat}
                note={
                  trigStat === 'saved' ? '' :
                  trigStat === 'partial' ? 'Some required trigger fields are NULL — see below' :
                  'No SCH_rule_triggers row exists for this rule'
                }
              >
                <MetadataRow label="Mode" value={trigger?.mode ? <strong>{trigger.mode}</strong> : ''} tone={!trigger ? 'warn' : undefined} />
                <MetadataRow label="Span" value={trigger?.span} />
                <MetadataRow label="Frequency" value={trigger?.frequency} tone={trigger?.mode === 'calendar' && !trigger.frequency ? 'warn' : undefined} />
                <MetadataRow label="Day of week" value={trigger?.day_of_week} />
                <MetadataRow label="Monthly specifier" value={trigger?.monthly_specifier} />
                <MetadataRow label="Nth weekday N" value={trigger?.nth_weekday_n} />
                <MetadataRow label="Nth weekday day" value={trigger?.nth_weekday_day} />
                <MetadataRow label="Specified day" value={trigger?.specified_day} />
                <MetadataRow label="Month of period" value={trigger?.month_of_period} />
                <MetadataRow label="Event" value={trigger?.event_id ? `${trigger.event?.name || ''} (#${trigger.event_id})` : ''} tone={trigger?.mode === 'event' && !trigger.event_id ? 'warn' : undefined} />
                <MetadataRow label="Variant" value={trigger?.variant} tone={trigger?.mode === 'event' && !trigger.variant ? 'warn' : undefined} />
                <MetadataRow label="Offset value" value={trigger?.offset_value} tone={trigger?.mode === 'event' && trigger.variant === 'after_event' && trigger.offset_value == null ? 'warn' : undefined} />
                <MetadataRow label="Offset unit" value={trigger?.offset_unit} />
                <MetadataRow label="Anchor from" value={trigger?.anchor_from} />
                <MetadataRow label="Watch grace days" value={trigger?.watch_grace_days} />
                <MetadataRow label="First fire mode" value={trigger?.first_fire_mode} />
                <MetadataRow label="Seed date" value={fmtDate(trigger?.seed_date)} />
                <MetadataRow label="Deadline kind" value={trigger?.deadline_kind} />
                <MetadataRow label="Deadline date" value={fmtDate(trigger?.deadline_date)} />
                <MetadataRow label="Deadline offset days" value={trigger?.deadline_offset_days} />
                <MetadataRow label="Deadline reference" value={trigger?.deadline_reference} />
                <MetadataRow label="Period token format" value={trigger?.period_token_format} tone="muted" />
              </SubCard>

              {/* 5. Scope — Who */}
              <SubCard
                number={5}
                title="Scope — Who (SCH_rule_scope + SCH_scope_locations)"
                status={scopeStat}
                note={
                  scopeStat === 'saved' ? '' :
                  scopeStat === 'partial' ? 'Scope row exists but no active geographies materialised' :
                  'No SCH_rule_scope row exists for this rule'
                }
              >
                <MetadataRow label="Scope mode" value={scope?.scope_mode} tone={!scope ? 'warn' : undefined} />
                <MetadataRow
                  label="Specific geographies"
                  value={
                    scope?.scope_mode === 'specific'
                      ? (scope.specific_locations && scope.specific_locations.length > 0
                          ? (
                              <>
                                <strong>{scope.specific_locations.length}</strong> picked
                                <div className="dry-meta-sub">
                                  {scope.specific_locations.slice(0, 8).map(l => l.location_name || `#${l.location_id}`).join(', ')}
                                  {scope.specific_locations.length > 8 && ` … +${scope.specific_locations.length - 8} more`}
                                </div>
                              </>
                            )
                          : (scope.specific_location_id
                              ? `${scope.specific_location_name || '?'} (#${scope.specific_location_id}) — legacy single-FK row`
                              : ''))
                      : ''
                  }
                  tone={scope?.scope_mode === 'specific' && !(scope.specific_locations && scope.specific_locations.length) && !scope.specific_location_id ? 'warn' : undefined}
                />
                <MetadataRow label="Region anchor" value={scope?.scope_mode === 'region_based' ? (scope.region_anchor_id ? `${scope.region_anchor_name || '?'} (#${scope.region_anchor_id})` : 'Global') : ''} />
                <MetadataRow label="Type filter" value={scope?.geography_type_name || scope?.type_filter} />
                <MetadataRow label="Filter annotation" value={scope?.filter_annotation} />
                <MetadataRow label="Materialised at" value={fmtDT(scope?.materialised_at)} tone="muted" />
                <MetadataRow label="Needs refresh" value={scope ? String(scope.needs_refresh) : ''} tone={scope?.needs_refresh ? 'warn' : 'muted'} />
                <MetadataRow
                  label="Locations (active)"
                  value={<><strong>{activeLocations.length}</strong>{missingLocations.length > 0 && (<> · <span style={{ color: '#b45309' }}>{missingLocations.length} missing</span></>)}</>}
                  tone={activeLocations.length === 0 ? 'warn' : undefined}
                />
              </SubCard>

              {/* 6. Materialised scope list — table inside SubCard */}
              <SubCard
                number={6}
                title={`Materialised scope list (${scope?.locations?.length || 0} row${(scope?.locations?.length || 0) === 1 ? '' : 's'})`}
                status={(scope?.locations?.length || 0) > 0 ? 'saved' : 'unsaved'}
                note={(scope?.locations?.length || 0) === 0 ? 'No materialised rows in SCH_scope_locations for this rule' : ''}
                grid={false}
              >
                {scope?.locations && scope.locations.length > 0 ? (
                  <div className="dry-iter-wrap">
                    <table className="table-list-table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th className="table-list-th table-list-cell--sno">S.No</th>
                          <th className="table-list-th">Location ID</th>
                          <th className="table-list-th">Name</th>
                          <th className="table-list-th">Type</th>
                          <th className="table-list-th">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scope.locations.map((l, i) => (
                          <tr key={`${l.location_id}-${i}`} className="table-list-row">
                            <td className="table-list-cell table-list-cell--sno">{i + 1}</td>
                            <td className="table-list-cell">#{l.location_id}</td>
                            <td className="table-list-cell">{l.location_name || DASH}</td>
                            <td className="table-list-cell">{l.location_type || DASH}</td>
                            <td className="table-list-cell">
                              <span className={`status-badge sm ${l.status === 'active' ? 'success' : 'neutral'}`}>{l.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="collapse-section__empty">
                    No materialised geographies. Configure Scope and save to populate this table.
                  </p>
                )}
              </SubCard>

              {/* 7. Applicability */}
              <SubCard
                number={7}
                title="Applicability — When applicable (SCH_cadence_rules + predicate rows)"
                status={applStat}
                note={applStat === 'unsaved' ? 'Defaulting to unconditional' : ''}
              >
                <MetadataRow label="Mode" value={applicability?.mode || rule.applicability_mode} />
                <MetadataRow label="Threshold" value={applicability?.threshold_id ? `${applicability.threshold?.name || '?'} (#${applicability.threshold_id})` : (rule.applicability_threshold_id ? `#${rule.applicability_threshold_id}` : '')} />
                <MetadataRow label="Questionnaire" value={applicability?.questionnaire_id ? `${applicability.questionnaire?.name || '?'} (#${applicability.questionnaire_id})` : (rule.applicability_questionnaire_id ? `#${rule.applicability_questionnaire_id}` : '')} />
                <MetadataRow label="Predicate combiner" value={applicability?.predicate_combiner || rule.predicate_combiner} />
                <MetadataRow label="Conditions" value={applicability?.conditions?.length ? `${applicability.conditions.length} condition(s)` : ''} />
                {applicability?.warnings && applicability.warnings.length > 0 && (
                  <MetadataRow label="Warnings" value={applicability.warnings.join(' · ')} tone="warn" />
                )}
              </SubCard>

              {/* 8. Date exceptions */}
              <SubCard
                number={8}
                title="Date exceptions (SCH_rule_date_exceptions)"
                status={dxStat}
                note={!dateExceptions ? 'No row — engine will use defaults (shift_forward, Mon-Fri, no calendar)' : ''}
              >
                <MetadataRow label="Action" value={dateExceptions?.action} />
                <MetadataRow label="Business days" value={fmtBusinessDays(dateExceptions?.business_days)} />
                <MetadataRow label="Holiday calendar" value={dateExceptions?.holiday_calendar_id ? `${dateExceptions.holiday_calendar?.name || '?'} (#${dateExceptions.holiday_calendar_id})` : ''} />
              </SubCard>

              {/* 9. Parameters */}
              <SubCard
                number={9}
                title="Parameters (SCH_rule_parameters)"
                status={paramsStat}
                note={!parameters ? 'No row — engine will use global defaults' : (parameters.mode === 'use_defaults' ? 'Inheriting tenant globals' : 'Using local overrides')}
              >
                <MetadataRow label="Mode" value={parameters?.mode} />
                <MetadataRow label="Holiday calendar" value={parameters?.holiday_calendar_id ? `#${parameters.holiday_calendar_id}` : ''} />
                <MetadataRow label="Business days" value={fmtBusinessDays(parameters?.business_day_definition)} />
                <MetadataRow label="Time zone" value={parameters?.time_zone} />
                <MetadataRow label="Reminder lead (days)" value={parameters?.reminder_lead_days} />
                <MetadataRow label="Grace period (days)" value={parameters?.grace_period_days} />
                <MetadataRow label="Catch-up window (days)" value={parameters?.catch_up_window_days} />
                {parameters?.catch_up_capped && (
                  <MetadataRow label="Catch-up capped" value={parameters.catch_up_cap_hint || 'yes'} tone="warn" />
                )}
              </SubCard>
            </>
          )}
      </Modal.Body>
      <Modal.Footer>
        <span className="dry-modal-footnote" style={{ marginRight: 'auto', fontSize: '0.78rem', color: '#64748b' }}>
          Dry-run is read-only — no register writes, no fire-queue inserts, no handler invocation.
        </span>
        <Button variant="outline" icon={RefreshCw} onClick={load} disabled={loading} data-testid="BTN_cadence_dry_run_reload">
          {loading ? 'Loading…' : 'Reload'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DryRunModal;
